import React from 'react';

const ALLOWED_TAGS = new Set([
    'a', 'b', 'strong', 'i', 'em', 'u', 's',
    'p', 'br', 'ul', 'ol', 'li', 'div', 'span',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'blockquote', 'code', 'pre',
]);

const DROP_WITH_CONTENT_TAGS = new Set([
    'script', 'style', 'template', 'noscript', 'iframe', 'object', 'embed',
    'svg', 'math', 'canvas', 'form', 'textarea', 'button', 'select',
]);

function safeHref(raw) {
    const href = String(raw ?? '').trim();
    if (!href || Array.from(href).some((character) => {
        const code = character.charCodeAt(0);
        return code <= 31 || code === 127;
    })) {
        return null;
    }

    if (href.startsWith('/') && !href.startsWith('//') && !href.startsWith('/\\')) {
        return href;
    }

    try {
        const base = typeof window !== 'undefined' ? window.location.origin : 'https://invalid.local';
        const parsed = new URL(href, base);
        return ['http:', 'https:', 'mailto:', 'tel:'].includes(parsed.protocol) ? href : null;
    } catch {
        return null;
    }
}

function renderNode(node, key) {
    if (node.nodeType === Node.TEXT_NODE) {
        const parts = (node.textContent ?? '').split(/\r\n?|\n/);
        if (parts.length === 1) {
            return parts[0];
        }

        return (
            <React.Fragment key={key}>
                {parts.map((part, index) => (
                    <React.Fragment key={`${key}.line.${index}`}>
                        {index > 0 ? <br /> : null}
                        {part}
                    </React.Fragment>
                ))}
            </React.Fragment>
        );
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
        return null;
    }

    const tag = node.tagName.toLowerCase();
    if (DROP_WITH_CONTENT_TAGS.has(tag)) {
        return null;
    }

    const children = Array.from(node.childNodes).map((child, index) => renderNode(child, `${key}.${index}`));
    if (!ALLOWED_TAGS.has(tag)) {
        return <React.Fragment key={key}>{children}</React.Fragment>;
    }

    if (tag === 'a') {
        const href = safeHref(node.getAttribute('href'));
        if (!href) {
            return <React.Fragment key={key}>{children}</React.Fragment>;
        }

        return <a key={key} href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
    }

    if (tag === 'br') {
        return React.createElement(tag, { key });
    }

    return React.createElement(tag, { key }, children);
}

export default function SafeMessageBody({ body }) {
    const value = String(body ?? '');
    if (typeof DOMParser === 'undefined') {
        return <>{value}</>;
    }

    try {
        const doc = new DOMParser().parseFromString(value, 'text/html');
        return <>{Array.from(doc.body.childNodes).map((node, index) => renderNode(node, String(index)))}</>;
    } catch {
        return <>{value}</>;
    }
}
